
import {PhoneHQIconAvatar, DsAvatar, FilterButton, FilterChip, FilterChips, PhoneHQGlyph, PhoneHQLoader, PhoneHQAvatar, SkeletonTable, useDelayedFlag, PhoneHQButton, PhoneHQCheckbox, PhoneHQDropdown, PhoneHQInput} from 'ch3-ui-lib';
import {activeContactFilters, withoutContactFilter} from "./contactFilterChips";
import {Colors} from "../../utilis/types/Types";
import {ChevronDown, CircleUser, Globe, LockKeyhole, MoreVertical, Search, X, Info, Trash2, PencilLine} from "lucide-react";
import {useIntl} from "react-intl";
import {ReactNode, useEffect, useMemo, useRef, useState} from "react";
import {useNavigate} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {FlagImage} from "react-international-phone";
import {getFlag} from "../../utilis/msisdnUtilis";
import {debounce} from 'lodash';
import useMediaQuery from "../../utilis/screenWidthUtils.ts";
import {User} from "../../store/features/Users/userService";
import {Group} from "../../store/features/groups/groupsService";
import {
    ContactListItem,
    useGetContactsPaginatedQuery,
    useDeleteContactByIdMutation,
    useAddExternalContactsBulkMutation,
} from "../../store/features/Contacts/contactsService";
import type {
    ExternalBulkContactError,
    ExternalBulkContactInput,
} from "../../store/features/Contacts/contactsService";
import {
    parseGoogleCsvContacts,
} from "./googleCsvImport";
import {parseVcardContacts} from "./vcardImport";
import type {ContactImportFailure} from "./contactImportTypes";
import DeleteConfirmDialog from "./DeleteConfirmDialog";
import {
    tagBadgeStyles,
} from "./helpers.tsx";
import {clsxMerge} from "../../utilis/classNameUtils.ts";
import {selectUserId} from "../../store/features/Auth/authSlice";
import {showToast} from "../../store/features/Notifications/notificationsSlice";
import {getFormatedPhoneNumber} from "../../utilis/msisdnUtilis";
import {getFullName} from "../../utilis/getFullName.ts";
import ContactsFilterPanel, {ContactFilters} from "./ContactsFilterPanel";
import ContactPanel from "./ContactPanel";
import ChangeAccessDialog from "./ChangeAccessDialog";
import ImportInfoDialog from "./ImportInfoDialog";
import OverflowChips from "./OverflowChips";
import {getPrivateAvailabilityRestriction} from "./contactAccessPolicy";

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

interface ExternalBulkContactRequestError {
    data?: {
        data?: ExternalBulkContactError[];
    };
}

function getExternalBulkContactFailures(
    error: unknown,
    contacts: ExternalBulkContactInput[],
): ContactImportFailure[] {
    const response = error as ExternalBulkContactRequestError;
    const errors = response?.data?.data;
    if (!Array.isArray(errors)) return [];

    const contactsByExternalId = new Map(
        contacts.map(contact => [contact.externalIdentifier.id, contact]),
    );

    return errors.map((contactError) => {
        const contact = contactsByExternalId.get(contactError.externalIdentifier.id);
        const name = [contact?.firstName, contact?.lastName]
            .filter(Boolean)
            .join(" ")
            || contact?.company
            || "Unnamed contact";
        const messages = contactError.errors.flatMap(validationError =>
            validationError.issues?.map(issue => issue.message) ?? [],
        );

        return {
            id: contactError.externalIdentifier.id,
            name,
            phoneNumbers: contact?.phoneNumbers.map(phone => phone.phoneNumber) ?? [],
            messages: messages.length > 0 ? messages : ["The contact could not be imported."],
        };
    });
}

const EmptyContacts = ({title, description, actions}: { title: string; description: string; actions: ReactNode }) => (
    <div className="flex flex-1 flex-col items-center justify-center gap-3 px-8 py-16 text-center">
        <span className="flex size-12 items-center justify-center rounded-[12px] bg-surface-muted text-ink-secondary">
            <CircleUser size={22} aria-hidden="true" className="!p-0 !text-current"/>
        </span>
        <h2 className="m-0 text-title text-ink">{title}</h2>
        <p className="m-0 max-w-[420px] text-callout text-ink-muted-strong">{description}</p>
        <div className="mt-1 flex gap-2">{actions}</div>
    </div>
);

export default function ContactsList() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const intl = useIntl();
    const isMobile = useMediaQuery('(max-width: 960px)');
    const userId = useSelector(selectUserId);

    // Search, sort, filter state
    const [searchValue, setSearchValue] = useState('');
    const [phrase, setPhrase] = useState('');
    const [sort, setSort] = useState<{ order?: string; property?: string }>({});

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);

    // Selection state
    const [selectedContacts, setSelectedContacts] = useState<Map<number, ContactListItem>>(new Map());

    // Bulk "change access" dialog state
    const [changeAccessOpen, setChangeAccessOpen] = useState(false);

    // Create contact panel state
    const [createPanelOpen, setCreatePanelOpen] = useState(false);

    // Edit contact panel state
    const [editContactId, setEditContactId] = useState<number | null>(null);

    // Filter panel state
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [activeFilters, setActiveFilters] = useState<ContactFilters | null>(null);

    // Delete modal state
    const [deleteContactId, setDeleteContactId] = useState<number | null>(null);

    const [deleteContact] = useDeleteContactByIdMutation();
    const [importContactsBulk, {isLoading: isImporting}] = useAddExternalContactsBulkMutation();
    const importFileInputRef = useRef<HTMLInputElement>(null);

    // Import info dialog state
    const [importDialogOpen, setImportDialogOpen] = useState(false);
    const [importFailures, setImportFailures] = useState<ContactImportFailure[]>([]);
    const [importedCount, setImportedCount] = useState(0);

    const handleImportClick = () => {
        setImportFailures([]);
        setImportedCount(0);
        setImportDialogOpen(true);
    };

    const handleChooseImportFile = () => {
        setImportFailures([]);
        setImportedCount(0);
        importFileInputRef.current?.click();
    };

    const handleImportFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file || !userId) return;
        const text = await file.text();
        const isVcard = /\.vcf$/i.test(file.name) || /^BEGIN:VCARD/im.test(text);
        const parseResult = isVcard
            ? parseVcardContacts(text, userId)
            : parseGoogleCsvContacts(text, userId);
        const {contacts, failures: parseFailures} = parseResult;

        if (contacts.length === 0) {
            setImportFailures(parseFailures);
            setImportedCount(0);
            setImportDialogOpen(true);
            return;
        }
        try {
            await importContactsBulk(contacts).unwrap();
            if (parseFailures.length > 0) {
                setImportFailures(parseFailures);
                setImportedCount(contacts.length);
                setImportDialogOpen(true);
            } else {
                dispatch(showToast({
                    description: `Imported ${contacts.length} contact${contacts.length === 1 ? "" : "s"}.`,
                    type: "success",
                }));
            }
        } catch (error) {
            const backendFailures = getExternalBulkContactFailures(error, contacts);
            if (backendFailures.length > 0) {
                setImportFailures([...parseFailures, ...backendFailures]);
                setImportedCount(Math.max(0, contacts.length - backendFailures.length));
                setImportDialogOpen(true);
            } else {
                dispatch(showToast({description: "Failed to import contacts.", type: "error"}));
            }
        }
    };

    const order = sort.order;
    const sortBy = sort.property;

    const offset = (currentPage - 1) * pageSize;

    const {data: contactsData, isLoading: isLoadingContacts, isFetching} = useGetContactsPaginatedQuery({
        phrase,
        order,
        sortBy,
        userId,
        offset,
        limit: pageSize,
        ...(activeFilters?.sharedForUsers?.length ? {sharedForUsers: activeFilters.sharedForUsers} : {}),
        ...(activeFilters?.sharedForGroups?.length ? {sharedForGroups: activeFilters.sharedForGroups} : {}),
        ...(activeFilters?.sources?.length ? {sources: activeFilters.sources} : {}),
        ...(activeFilters?.dateFrom ? {dateFrom: activeFilters.dateFrom} : {}),
        ...(activeFilters?.dateTo ? {dateTo: activeFilters.dateTo} : {}),
    });

    const contacts = contactsData?.contacts ?? [];
    const totalCount = contactsData?.totalCount ?? 0;
    const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
    const showStart = totalCount > 0 ? offset + 1 : 0;
    const showEnd = Math.min(offset + pageSize, totalCount);

    const loading = isLoadingContacts;
    const showSkeleton = useDelayedFlag(loading);

    // Handlers
    const handleDataSortChange = (order: string, property: string) => {
        setCurrentPage(1);
        setSort({order, property});
    };

    const debouncedSetPhrase = useMemo(
        () => debounce((newPhrase: string) => {
            setCurrentPage(1);
            setPhrase(newPhrase);
        }, 1000),
        []
    );

    useEffect(() => () => debouncedSetPhrase.cancel(), [debouncedSetPhrase]);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchValue(value);
        debouncedSetPhrase(value);
    };

    const handleClearSearch = () => {
        debouncedSetPhrase.cancel();
        setSearchValue('');
        setPhrase('');
        setCurrentPage(1);
    };

    const handleClearFilters = () => {
        setActiveFilters(null);
        setCurrentPage(1);
    };

    const handlePageSizeChange = (newSize: number) => {
        setPageSize(newSize);
        setCurrentPage(1);
    };

    // Selection handlers
    const isAllSelected = contacts.length > 0 && contacts.every((c: ContactListItem) => selectedContacts.has(c.contactId));
    const isSomeSelected = contacts.some((c: ContactListItem) => selectedContacts.has(c.contactId));

    const handleSelectAll = () => {
        if (isAllSelected) {
            const next = new Map(selectedContacts);
            contacts.forEach((c: ContactListItem) => next.delete(c.contactId));
            setSelectedContacts(next);
        } else {
            const next = new Map(selectedContacts);
            contacts.forEach((contact: ContactListItem) => next.set(contact.contactId, contact));
            setSelectedContacts(next);
        }
    };

    const handleSelectContact = (contact: ContactListItem) => {
        const next = new Map(selectedContacts);
        if (next.has(contact.contactId)) {
            next.delete(contact.contactId);
        } else {
            next.set(contact.contactId, contact);
        }
        setSelectedContacts(next);
    };

    const handleClearSelection = () => {
        setSelectedContacts(new Map());
    };

    // External (SIM / integration) contacts can't be edited — same guard as single edit.
    const selectedExternalContacts = Array.from(selectedContacts.values()).filter(
        contact => contact.contactType?.type === 'EXTERNAL'
    );

    const handleOpenChangeAccess = () => {
        if (selectedExternalContacts.length > 0) {
            dispatch(showToast({
                description: "Some selected contacts (e.g. SIM or integration contacts) can't be edited.",
                type: 'error',
            }));
            return;
        }
        setChangeAccessOpen(true);
    };

    const handleDeleteContact = async () => {
        if (deleteContactId !== null) {
            await deleteContact(deleteContactId);
        }
        setDeleteContactId(null);
    };

    const openDeleteModal = (contactId: number) => {
        setDeleteContactId(contactId);
    };

    const handleBulkDelete = async () => {
        const ids = Array.from(selectedContacts.keys());
        await Promise.all(ids.map(id => deleteContact(id)));
        setSelectedContacts(new Map());
    };

    const handleApplyFilters = (filters: ContactFilters) => {
        setActiveFilters(activeContactFilters(filters).length ? filters : null);
        setCurrentPage(1);
    };

    const activeFilterChips: FilterChip[] = activeContactFilters(activeFilters).map(({key, label}) => ({
        key,
        label,
        onRemove: () => {
            setActiveFilters((current) => current && withoutContactFilter(current, key));
            setCurrentPage(1);
        },
    }));

    const sortOptions = [
        {key: 'created', label: 'Created', value: {order: 'DESC', property: 'CREATED_AT'}},
        {key: 'name-asc', label: 'Name A-Z', value: {order: 'ASC', property: 'FIRST_NAME'}},
        {key: 'name-desc', label: 'Name Z-A', value: {order: 'DESC', property: 'FIRST_NAME'}},
    ];

    const currentSortLabel = sortOptions.find(
        option => option.value.order === sort.order && option.value.property === sort.property,
    )?.label ?? 'Created';

    const hasSelection = selectedContacts.size > 0;
    const selectedContactValues = useMemo(() => Array.from(selectedContacts.values()), [selectedContacts]);
    const privateRestriction = useMemo(
        () => getPrivateAvailabilityRestriction(userId, selectedContactValues),
        [selectedContactValues, userId],
    );

    return (
        <>
            {loading && showSkeleton && (
                <div className={isMobile ? 'px-4' : 'px-8 pt-[128px]'}>
                    <SkeletonTable
                        label="Loading contacts"
                        gridTemplateColumns="48px 22% 36% 1fr 56px"
                        cells={[null, {width: 120, avatar: true}, {width: 120}, {width: 64}, null]}
                    />
                </div>
            )}
            {(!loading && contactsData) &&
                <div className="w-full">
                    <div className={isMobile ? 'px-4' : 'px-8'}>
                        {/* Page header */}
                        <header className="flex h-[72px] items-center gap-3">
                            <span className="flex size-9 shrink-0 items-center justify-center rounded-control bg-ink-secondary text-white">
                                <CircleUser size={20} aria-hidden="true" className="!p-0 !text-current"/>
                            </span>
                            <h1 className="m-0 text-[24px] font-medium leading-tight text-ink">
                                {intl.formatMessage({id: 'contacts.menu', defaultMessage: 'Contacts'})}
                            </h1>
                        </header>

                        {/* Toolbar */}
                        <div className="flex min-h-14 flex-wrap items-center gap-2">
                            <div className={`relative ${isMobile ? 'w-full' : 'w-[240px]'}`}>
                                <PhoneHQInput
                                    placeholder="Search…"
                                    aria-label="Search contacts"
                                    className={`pl-9 text-callout ${isMobile ? 'w-full' : 'w-[240px]'} h-control-md rounded-control`}
                                    value={searchValue}
                                    onChange={handleSearchChange}
                                    prefixIcon={<Search size={16}/>}
                                    suffixIcon={searchValue ? (
                                        <PhoneHQButton
                                            variant="tertiary"
                                            size={'iconXs'}
                                            onClick={handleClearSearch}
                                        >
                                            <X size={15} className="!p-0"/>
                                        </PhoneHQButton>
                                    ) : undefined}
                                />
                            </div>

                            <PhoneHQDropdown
                                trigger={
                                    <PhoneHQButton variant="secondary">
                                        Sort by: {currentSortLabel} <ChevronDown size={16} className="!p-0"/>
                                    </PhoneHQButton>
                                }
                            >
                                {sortOptions.map(opt => (
                                    <PhoneHQDropdown.Item
                                        key={opt.key}
                                        onClick={() => handleDataSortChange(opt.value.order, opt.value.property)}
                                    >
                                        {opt.label}
                                    </PhoneHQDropdown.Item>
                                ))}
                            </PhoneHQDropdown>

                            <FilterButton count={activeFilterChips.length} onClick={() => setFiltersOpen(true)}/>

                            <FilterChips
                                className="ml-1"
                                chips={activeFilterChips}
                                onClearAll={handleClearFilters}
                            />

                            <div className="flex-1"/>

                            <PhoneHQButton variant="secondary" onClick={handleImportClick} disabled={isImporting}>
                                {isImporting ? "Importing…" : "Import"}
                            </PhoneHQButton>
                            <input
                                ref={importFileInputRef}
                                type="file"
                                accept=".csv,.vcf,text/csv,text/vcard,text/x-vcard"
                                className="hidden"
                                onChange={handleImportFileChange}
                            />

                            <PhoneHQButton variant="primary" onClick={() => setCreatePanelOpen(true)}>
                                Create contact
                            </PhoneHQButton>
                        </div>

                        {/* Table area */}
                        <div className="-mx-8 flex h-[calc(100vh-128px-var(--header-height))] flex-col">
                            {contacts.length > 0 && (
                                <div className="flex-1 overflow-y-auto">
                                    <div className={clsxMerge('flex flex-col overflow-hidden', hasSelection && 'bg-brand-tint-subtle')}>
                                    {/* Bulk actions bar */}
                                    {hasSelection && (
                                        <div className="flex h-10 items-center gap-3 border-0 border-y border-solid border-line bg-brand-tint-subtle px-4">
                                            <PhoneHQCheckbox
                                                value=""
                                                checked={isAllSelected ? true : "indeterminate"}
                                                onCheckedChange={handleSelectAll}
                                            />
                                            <span className="text-footnote font-semibold text-ink">
                                                {selectedContacts.size} selected contact{selectedContacts.size !== 1 ? 's' : ''}
                                            </span>
                                            <PhoneHQButton variant="secondary" size="sm" className="!border-brand-disabled !text-brand-ink"
                                                           onClick={handleOpenChangeAccess}>
                                                Change access
                                            </PhoneHQButton>
                                            <DeleteConfirmDialog
                                                onConfirm={handleBulkDelete}
                                                trigger={
                                                    <PhoneHQButton variant="secondary" size="sm"
                                                                   className="!border-danger-tint !text-danger-ink">
                                                        Delete
                                                    </PhoneHQButton>
                                                }
                                                description={
                                                    <>
                                                        <p className="leading-5 mb-4">
                                                            This action will permanently remove {selectedContacts.size} contact{selectedContacts.size !== 1 ? 's' : ''} and all associated data.
                                                        </p>
                                                        <p className="leading-5">This cannot be undone.</p>
                                                    </>
                                                }
                                            />
                                            <div className="flex-1"/>
                                            <button type="button" className={'cursor-pointer text-footnote font-semibold text-brand-ink hover:underline'}
                                                           onClick={()=> handleClearSelection()}>
                                                Clear selection
                                            </button>
                                        </div>
                                    )}

                                    {/* Table card — single table so header & body columns stay aligned
                                        while sizing to their content (table-auto). */}
                                    <div className={'overflow-hidden bg-surface'}>
                                        <table className="w-full table-fixed border-collapse">
                                            {!hasSelection && (
                                                <thead>
                                                <tr className="h-10 border-0 border-y border-solid border-line [&>th]:bg-canvas [&>th]:text-left [&>th]:text-caption [&>th]:text-ink-muted-strong">
                                                    <th scope="col" className="w-[48px] pl-4">
                                                        <PhoneHQCheckbox
                                                            value=""
                                                            checked={isAllSelected ? true : isSomeSelected ? "indeterminate" : false}
                                                            onCheckedChange={handleSelectAll}
                                                        />
                                                    </th>
                                                    <th scope="col" className="w-[22%] pr-4">
                                                        {intl.formatMessage({id: 'contacts.name'})}
                                                    </th>
                                                    <th scope="col" className="w-[36%] pr-4">
                                                        {intl.formatMessage({id: 'contacts.phone-number'})}
                                                    </th>
                                                    {/* Flexible column: takes the remaining space and is the first
                                                        to collapse its chips to +N as the window narrows. */}
                                                    <th scope="col" className="text-left">
                                                        Shared for
                                                    </th>
                                                    <th scope="col" className="w-[56px]"/>
                                                </tr>
                                                </thead>
                                            )}
                                            <tbody >
                                            {contacts.map((contact: ContactListItem) => {
                                                const isSelected = selectedContacts.has(contact.contactId);
                                                return (
                                                    <tr key={contact.contactId}
                                                        onClick={() => navigate(`/contacts/details/${contact.contactId}`)}
                                                        className={`group h-11 cursor-pointer border-0 border-b border-solid border-line ${isSelected ? '[&>td]:bg-brand-tint-subtle' : '[&:hover>td]:bg-surface-muted'}`}>
                                                        {/* Checkbox */}
                                                        <td className="w-[48px] pl-4" onClick={(e) => e.stopPropagation()}>
                                                            <div className={`${isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity`}>
                                                                <PhoneHQCheckbox
                                                                    value=""
                                                                    checked={isSelected}
                                                                    onCheckedChange={() => handleSelectContact(contact)}
                                                                />
                                                            </div>
                                                        </td>

                                                        {/* Name */}
                                                        <td className="w-[22%] pr-4">
                                                            <div className="no-underline flex items-center gap-2.5 min-w-0">
                                                                <DsAvatar size={28} name={getFullName(contact.firstName, contact.lastName)}/>
                                                                <span className="truncate text-callout font-medium text-ink">
                                                                    {getFullName(contact.firstName, contact.lastName)}
                                                                </span>
                                                            </div>
                                                        </td>

                                                        {/* Phone Numbers — fits as many chips as the column allows,
                                                            collapsing the rest into a +N hover badge. */}
                                                        <td className="w-[36%] pr-4 overflow-hidden">
                                                            {contact.phoneNumbers?.length > 0 && (
                                                                <OverflowChips
                                                                    items={contact.phoneNumbers}
                                                                    badgeClassName={'bg-surface border border-solid border-line rounded-chip h-7 px-2 gap-1.5 inline-flex items-center text-footnote font-medium text-ink'}
                                                                    renderChip={(el) => (
                                                                        <span className={'bg-surface border border-solid border-line rounded-chip h-7 px-2 gap-1.5 inline-flex items-center text-footnote font-medium text-ink' + ' shrink-0'}>
                                                                            <FlagImage className="w-3 h-3" iso2={getFlag(el.phoneNumber) ?? 'us'}/>
                                                                            <span>{getFormatedPhoneNumber(el.phoneNumber, true)}</span>
                                                                        </span>
                                                                    )}
                                                                    renderTooltipItem={(el) => (
                                                                        <div className="flex items-center gap-3 px-3 py-2">
                                                                            <FlagImage className="size-[18px] shrink-0" iso2={getFlag(el.phoneNumber) ?? 'us'}/>
                                                                            <span className="text-footnote font-semibold leading-4 text-white whitespace-nowrap">{getFormatedPhoneNumber(el.phoneNumber, true)}</span>
                                                                        </div>
                                                                    )}
                                                                />
                                                            )}
                                                        </td>

                                                        {/* Tags — greedy column (no width), collapses first */}
                                                        <td className="min-w-0">
                                                            <div className="flex items-center gap-2">
                                                                {contact.availability === "PUBLIC" && (
                                                                    <span className={clsxMerge(tagBadgeStyles({color: 'indigo'}))}>
                                                                        <Globe size={14} className="!p-0 !text-current"/>
                                                                        Public
                                                                    </span>
                                                                )}
                                                                {contact.availability === "PRIVATE" && (
                                                                    <span className={clsxMerge(tagBadgeStyles({color: 'zinc'}))}>
                                                                        <LockKeyhole size={14} className="!p-0 !text-current"/>
                                                                        Only you
                                                                    </span>
                                                                )}
                                                                {contact.availability === "SHARED" && (
                                                                    <OverflowChips
                                                                        items={[...(contact.sharedForGroups ?? []), ...(contact.sharedForUsers ?? [])]}
                                                                        badgeClassName="inline-flex h-7 items-center rounded-chip border border-solid border-line px-2 text-footnote font-semibold text-ink-muted-strong"
                                                                        renderChip={(item: Group | User) => {
                                                                            const isGroup = "name" in item;
                                                                            const label = isGroup
                                                                                ? item.name
                                                                                : getFullName(item.firstName, item.lastName);
                                                                            return (
                                                                                <div className="inline-flex h-7 shrink-0 items-center gap-1.5 rounded-chip bg-surface-muted px-2 text-footnote font-medium text-ink-secondary">
                                                                                    {isGroup ? (
                                                                                        <PhoneHQIconAvatar icon={item.icon ?? 'group'} color={item.iconColor ?? Colors.grey} size="20px"/>
                                                                                    ) : (
                                                                                        <PhoneHQAvatar size="20px" type="initials" name={label} colorFromName/>
                                                                                    )}
                                                                                    <span>{label}</span>
                                                                                </div>
                                                                            );
                                                                        }}
                                                                        renderTooltipItem={(item: Group | User) => {
                                                                            const isGroup = "name" in item;
                                                                            const label = isGroup
                                                                                ? item.name
                                                                                : getFullName(item.firstName, item.lastName);
                                                                            return (
                                                                                <div className="flex items-center gap-2">
                                                                                    {isGroup ? (
                                                                                        <PhoneHQIconAvatar icon={item.icon ?? 'group'} color={item.iconColor ?? Colors.grey} size="20px"/>
                                                                                    ) : (
                                                                                        <PhoneHQAvatar size="20px" type="initials" name={label} colorFromName/>
                                                                                    )}
                                                                                    <span>{label}</span>
                                                                                </div>
                                                                            );
                                                                        }}
                                                                    />
                                                                )}
                                                            </div>
                                                        </td>

                                                        {/* Actions */}
                                                        <td className="w-[56px] text-center" onClick={(e) => e.stopPropagation()}>
                                                            <PhoneHQDropdown
                                                                trigger={
                                                                    <button type="button" aria-label="Row actions" className="inline-flex size-7 cursor-pointer items-center justify-center rounded-chip text-ink-secondary hover:bg-line">
                                                                        <MoreVertical size={16} aria-hidden="true" className="!p-0 !text-current"/>
                                                                    </button>
                                                                }
                                                                align="end"
                                                                showCloseIcon
                                                            >
                                                                <PhoneHQDropdown.Item onClick={() => navigate(`/contacts/details/${contact.contactId}`)}>
                                                                    <Info size={18} className={'text-ink-muted group-hover:text-white transition-colors'} />
                                                                    <span className={'text-tiny px-3 w-40'}>View</span>
                                                                </PhoneHQDropdown.Item>
                                                                <PhoneHQDropdown.Item onClick={() => setEditContactId(contact.contactId)}>
                                                                    <PencilLine size={18} className={'text-ink-muted group-hover:text-white transition-colors'}/>
                                                                    <span className={'text-tiny px-3 w-40'}>Edit</span>
                                                                </PhoneHQDropdown.Item>
                                                                <PhoneHQDropdown.Item onClick={() => openDeleteModal(contact.contactId)}>
                                                                    <Trash2 size={18} className={'text-menu-danger'} />
                                                                    <span className={'text-tiny px-3 w-40 text-menu-danger'}>Delete</span>
                                                                </PhoneHQDropdown.Item>
                                                            </PhoneHQDropdown>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                            </tbody>
                                        </table>
                                    </div>
                                    </div>
                                </div>
                            )}

                            {/* Empty state - search */}
                            {contacts.length === 0 && phrase && !isFetching && (
                                <EmptyContacts
                                    title="No contacts found"
                                    description={`Nothing matches “${phrase}”. Check the spelling or add a new contact.`}
                                    actions={<PhoneHQButton variant="secondary" onClick={handleClearSearch}>Clear search</PhoneHQButton>}
                                />
                            )}

                            {/* Empty state - no contacts at all */}
                            {(!totalCount && !phrase && !isFetching && contacts.length === 0) && (
                                <EmptyContacts
                                    title="No contacts yet"
                                    description="Add your first contact or import them from a file. Shared contacts are visible to the people you choose."
                                    actions={
                                        <>
                                            <PhoneHQButton variant="secondary" onClick={handleImportClick} disabled={isImporting}>
                                                {isImporting ? "Importing…" : "Import"}
                                            </PhoneHQButton>
                                            <PhoneHQButton variant="primary" onClick={() => setCreatePanelOpen(true)}>
                                                Create contact
                                            </PhoneHQButton>
                                        </>
                                    }
                                />
                            )}

                            {isFetching && !isLoadingContacts && <PhoneHQLoader/>}

                            {/* Pagination bar — hidden when everything fits on one page */}
                            {totalCount > 0 && totalPages > 1 && (
                                <div className="mt-auto flex h-12 shrink-0 items-center justify-between border-0 border-t border-solid border-line px-8 text-footnote font-medium text-ink-secondary">
                                    <span>
                                        Showing {showStart} – {showEnd} of {totalCount} contacts
                                    </span>

                                    <div className="flex items-center gap-4">
                                        <div className="flex items-center gap-2">
                                            <PhoneHQDropdown
                                                trigger={
                                                    <PhoneHQButton variant="secondary" size="sm" className="!h-7 !rounded-chip !px-2">
                                                        {pageSize} <ChevronDown className={'!p-0'} size={14}/>
                                                    </PhoneHQButton>
                                                }
                                            >
                                                {PAGE_SIZE_OPTIONS.map(size => (
                                                    <PhoneHQDropdown.Item
                                                        key={size}
                                                        onClick={() => handlePageSizeChange(size)}
                                                    >
                                                        {size}
                                                    </PhoneHQDropdown.Item>
                                                ))}
                                            </PhoneHQDropdown>
                                            <span>per page</span>
                                        </div>

                                        <div className="h-5 w-px bg-line"/>

                                        <div className="flex items-center gap-2">
                                            <span>
                                                Page {currentPage} of {totalPages}
                                            </span>
                                            <PhoneHQButton
                                                variant="secondary"
                                                size="icon"
                                                aria-label="Previous page"
                                                className="!size-7 !rounded-chip"
                                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                                disabled={currentPage <= 1}
                                            >
                                                <PhoneHQGlyph name="chevron_left" size={16}/>
                                            </PhoneHQButton>
                                            <PhoneHQButton
                                                variant="secondary"
                                                size="icon"
                                                aria-label="Next page"
                                                className="!size-7 !rounded-chip"
                                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                                disabled={currentPage >= totalPages}
                                            >
                                                <PhoneHQGlyph name="chevron_right" size={16}/>
                                            </PhoneHQButton>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            }

            {/* Delete single contact modal */}
            <DeleteConfirmDialog
                open={deleteContactId !== null}
                onOpenChange={(next) => { if (!next) setDeleteContactId(null); }}
                onConfirm={handleDeleteContact}
            />

            <ContactsFilterPanel
                open={filtersOpen}
                onClose={() => setFiltersOpen(false)}
                onApply={handleApplyFilters}
                initialFilters={activeFilters ?? undefined}
                sourceCounts={contactsData?.sourceCounts}
            />

            <ContactPanel
                open={createPanelOpen}
                onClose={() => setCreatePanelOpen(false)}
            />

            {editContactId !== null && (
                <ContactPanel
                    open={editContactId !== null}
                    onClose={() => setEditContactId(null)}
                    contactId={editContactId}
                />
            )}

            <ChangeAccessDialog
                open={changeAccessOpen}
                onClose={() => setChangeAccessOpen(false)}
                contactIds={Array.from(selectedContacts.keys())}
                onSuccess={handleClearSelection}
                privateRestriction={privateRestriction}
            />

            <ImportInfoDialog
                open={importDialogOpen}
                onOpenChange={setImportDialogOpen}
                onChooseFile={handleChooseImportFile}
                failures={importFailures}
                importedCount={importedCount}
            />
        </>
    );
}
